import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function PrivacyPolicyPage() {
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation('privacy');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formatDate = () => {
    const date = new Date();
    const locale = i18n.language === 'nl' ? 'nl-NL' : i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />
      
      <div className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-teal-500 hover:text-teal-600 transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            {t('backToHome')}
          </Link>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {t('title')}
          </h1>
          <p className="text-slate-600 mb-12">
            {t('lastUpdated')} {formatDate()}
          </p>

          <div className="space-y-10">
            {/* 1. Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.introduction.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed">
                {t('sections.introduction.content')}
              </p>
            </section>

            {/* 2. Company Info */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.company.title')}
              </h2>
              <div className="bg-slate-50 p-6 rounded-lg space-y-2">
                <p className="text-slate-700"><strong>{t('sections.company.content')}</strong></p>
                <p className="text-slate-700">{t('sections.company.address')}</p>
                <p className="text-slate-700">{t('sections.company.vat')}</p>
                <p className="text-slate-700">{t('sections.company.enterprise')}</p>
                <p className="text-slate-700">{t('sections.company.email')}</p>
                <p className="text-slate-700">{t('sections.company.phone')}</p>
              </div>
            </section>

            {/* 3. Data Collection */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.dataCollection.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.dataCollection.intro')}
              </p>
              <p className="text-slate-700 font-semibold mb-3">
                {t('sections.dataCollection.subtitle')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mb-4">
                <li>{t('sections.dataCollection.items.company')}</li>
                <li>{t('sections.dataCollection.items.phone')}</li>
                <li>{t('sections.dataCollection.items.address')}</li>
                <li>{t('sections.dataCollection.items.vat')}</li>
              </ul>
              <p className="text-slate-700 italic">
                {t('sections.dataCollection.note')}
              </p>
            </section>

            {/* 4. Data Source */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.dataSource.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.dataSource.content')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mb-4">
                {t('sections.dataSource.sources', { returnObjects: true }).map((source: string, index: number) => (
                  <li key={index}>{source}</li>
                ))}
              </ul>
              <p className="text-slate-700 italic">
                {t('sections.dataSource.note')}
              </p>
            </section>

            {/* 5. Data Usage */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.dataUsage.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.dataUsage.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                {t('sections.dataUsage.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 6. Legal Basis */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.legalBasis.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.legalBasis.intro')}
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.legalBasis.content')}
              </p>
              <div className="bg-teal-50 p-6 rounded-lg">
                <p className="text-slate-900 font-semibold mb-3">
                  {t('sections.legalBasis.balancing.title')}
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li>• {t('sections.legalBasis.balancing.business')}</li>
                  <li>• {t('sections.legalBasis.balancing.privacy')}</li>
                  <li>• {t('sections.legalBasis.balancing.noPersonal')}</li>
                  <li>• {t('sections.legalBasis.balancing.objection')}</li>
                </ul>
              </div>
            </section>

            {/* 7. Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.retention.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed">
                {t('sections.retention.content')}
              </p>
            </section>

            {/* 8. Data Sharing */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.dataSharing.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.dataSharing.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mb-4">
                {t('sections.dataSharing.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-slate-700 font-semibold">
                {t('sections.dataSharing.euOnly')}
              </p>
            </section>

            {/* 9. Your Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.yourRights.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.yourRights.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mb-6">
                <li><strong>{t('sections.yourRights.items.access').split(':')[0]}:</strong> {t('sections.yourRights.items.access').split(':').slice(1).join(':')}</li>
                <li><strong>{t('sections.yourRights.items.rectification').split(':')[0]}:</strong> {t('sections.yourRights.items.rectification').split(':').slice(1).join(':')}</li>
                <li><strong>{t('sections.yourRights.items.erasure').split(':')[0]}:</strong> {t('sections.yourRights.items.erasure').split(':').slice(1).join(':')}</li>
                <li><strong>{t('sections.yourRights.items.restriction').split(':')[0]}:</strong> {t('sections.yourRights.items.restriction').split(':').slice(1).join(':')}</li>
                <li><strong>{t('sections.yourRights.items.objection').split(':')[0]}:</strong> {t('sections.yourRights.items.objection').split(':').slice(1).join(':')}</li>
              </ul>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
                <p className="text-slate-900 font-semibold mb-2">
                  {t('sections.yourRights.objectionNote.title')}
                </p>
                <p className="text-slate-700">
                  {t('sections.yourRights.objectionNote.content')}
                </p>
              </div>

              <div className="bg-slate-50 p-6 rounded-lg">
                <p className="text-slate-900 font-semibold mb-3">
                  {t('sections.yourRights.howTo.title')}
                </p>
                <p className="text-slate-700 mb-3">
                  {t('sections.yourRights.howTo.content')}
                </p>
                <p className="text-slate-700 mb-2">
                  <strong>{t('sections.yourRights.howTo.email')}</strong> <a href="mailto:David@novaventure.be" className="text-teal-600 hover:text-teal-700 cursor-pointer">David@novaventure.be</a>
                </p>
                <p className="text-slate-700">
                  {t('sections.yourRights.howTo.response')}
                </p>
              </div>
            </section>

            {/* 10. Data Security */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.dataSecurity.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.dataSecurity.content')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                {t('sections.dataSecurity.measures', { returnObjects: true }).map((measure: string, index: number) => (
                  <li key={index}>{measure}</li>
                ))}
              </ul>
            </section>

            {/* 11. Complaint */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.complaint.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.complaint.content')}
              </p>
              <div className="bg-slate-50 p-6 rounded-lg space-y-2">
                <p className="text-slate-700"><strong>{t('sections.complaint.authority')}</strong></p>
                <p className="text-slate-700">{t('sections.complaint.addressLine1')}</p>
                <p className="text-slate-700">{t('sections.complaint.phone')}</p>
                <p className="text-slate-700">{t('sections.complaint.email')}</p>
                <p className="text-slate-700">{t('sections.complaint.website')}</p>
              </div>
            </section>

            {/* 12. Changes */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.changes.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed">
                {t('sections.changes.content')}
              </p>
            </section>

            {/* 13. Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('sections.contact.title')}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('sections.contact.intro')}
              </p>
              <div className="bg-slate-50 p-6 rounded-lg space-y-3">
                <p className="text-slate-700"><strong>{t('sections.contact.company')}</strong></p>
                <p className="text-slate-700">{t('sections.contact.address')}</p>
                <p className="text-slate-700 flex items-center gap-2">
                  <i className="ri-phone-line text-teal-500"></i>
                  <strong>{t('sections.contact.phone')}</strong>
                  <a href="tel:+32488139664" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                    +32 488 13 96 64
                  </a>
                </p>
                <p className="text-slate-700 flex items-center gap-2">
                  <i className="ri-mail-line text-teal-500"></i>
                  <strong>{t('sections.contact.email')}</strong>
                  <a href="mailto:david@novaventure.be" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                    david@novaventure.be
                  </a>
                </p>
                <p className="text-slate-700">{t('sections.contact.vat')}</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}